// Derived views for the marketplace app. Nothing here is stored: each is a fold over admitted claims,
// and every value carries the claim ids it came from.

import type { ActorId, Claim, ClaimId } from '../claim.ts';
import type { Scope } from '../scope.ts';

const of = (scope: Scope, predicate: string): Claim[] => (scope.index.byPredicate.get(predicate) ?? []).map((e) => e.claim);
// v2.1: a reference is refs[0] (what a rule binds as $ref); a mention is any position. Views read them the way the
// policy does: complete / release / withdraw / accept / respond / appeal refer; a decision mentions every report it
// closes and the decision it answers.
const refersTo = (scope: Scope, id: ClaimId, predicates: string[]): Claim[] =>
  (scope.index.refsTo.get(id) ?? []).map((e) => e.claim).filter((c) => c.refs[0] === id && predicates.includes(c.predicate));
const mentionedBy = (scope: Scope, id: ClaimId, predicates: string[]): Claim[] =>
  (scope.index.refsTo.get(id) ?? []).map((e) => e.claim).filter((c) => predicates.includes(c.predicate));

// The scope's own notion of now: the admission time of its last entry. Views take an explicit `now` when they have one.
const lastAt = (scope: Scope): number => scope.log.at(-1)?.at ?? 0;

export interface Interaction {
  id: ClaimId; // the accept claim
  offer: ClaimId;
  buyer: ActorId;
  seller: ActorId;
  completedBy: ActorId[];
  done: boolean;
  released: ClaimId | null; // R8: the release that let the offer go, if any
}

export function interactions(scope: Scope): Interaction[] {
  return of(scope, 'accept').map((a) => {
    const offer = scope.entry(a.refs[0]!)!.claim;
    const completedBy = [...new Set(refersTo(scope, a.id, ['complete']).map((c) => c.author))];
    const released = refersTo(scope, a.id, ['release'])[0]?.id ?? null;
    return { id: a.id, offer: offer.id, buyer: a.author, seller: offer.author, completedBy, done: completedBy.length >= 2, released };
  });
}

// Standing sanctions on an actor: unexpired suspend-offers decisions that no later decision (an appeal) superseded.
export function activeSanctions(scope: Scope, actor: ActorId, now: number): Claim[] {
  return of(scope, 'decide').filter(
    (d) =>
      d.subject === actor &&
      (d.body as { outcome?: string }).outcome === 'suspend-offers' &&
      Number((d.body as { until?: number }).until ?? 0) > now &&
      mentionedBy(scope, d.id, ['decide']).length === 0,
  );
}

// Offers the viewer may see that are neither withdrawn nor under an unreleased accept, by sellers who are not paused (R5).
export function liveOffers(scope: Scope, viewer: ActorId, now: number = lastAt(scope)): Claim[] {
  const visible = new Set(scope.visible(viewer).map((c) => c.id));
  const paused = new Set<ActorId>();
  const live = (o: Claim): boolean =>
    refersTo(scope, o.id, ['withdraw']).length === 0 && refersTo(scope, o.id, ['accept']).every((a) => refersTo(scope, a.id, ['release']).length > 0);
  return of(scope, 'offer').filter((o) => {
    if (!visible.has(o.id) || !live(o)) return false;
    if (!paused.has(o.author) && activeSanctions(scope, o.author, now).length > 0) paused.add(o.author);
    return !paused.has(o.author);
  });
}

export interface Standing {
  actor: ActorId;
  completed: ClaimId[];
  ratingsReceived: { id: ClaimId; score: number; response: ClaimId | null }[];
  openReports: ClaimId[];
  sanctions: ClaimId[];
  replies: ClaimId[]; // R4: the actor's answers on the record (to ratings, reports, decisions)
}

// Standing is per scope and is a list of evidence, not a number. A UI may summarise it; the summary is never stored.
export function standing(scope: Scope, actor: ActorId, now: number): Standing {
  const completed = interactions(scope)
    .filter((i) => i.done && (i.buyer === actor || i.seller === actor))
    .map((i) => i.id);
  const ratingsReceived = of(scope, 'rate')
    .filter((r) => r.subject === actor)
    .map((r) => ({
      id: r.id,
      score: Number((r.body as { score: number }).score),
      response: refersTo(scope, r.id, ['respond'])[0]?.id ?? null,
    }));
  const reports = of(scope, 'report').filter((r) => r.subject === actor);
  const openReports = reports.filter((r) => mentionedBy(scope, r.id, ['decide']).length === 0).map((r) => r.id);
  const sanctions = activeSanctions(scope, actor, now).map((d) => d.id);
  const replies = of(scope, 'respond')
    .filter((r) => r.author === actor)
    .map((r) => r.id);
  return { actor, completed, ratingsReceived, openReports, sanctions, replies };
}

export interface Case {
  report: Claim;
  evidence: Claim[]; // promoted artifacts
  flags: Claim[]; // AI triage: advisory only
  decision: Claim | null;
  replies: Claim[]; // R4: the reported person's answers to the report and to the decision
  appeal: Claim | null; // R4
  appealDecision: Claim | null; // R4: by a steward other than the original decider
}

export function caseOf(scope: Scope, reportId: ClaimId, viewer: ActorId): Case | null {
  const visible = new Set(scope.visible(viewer).map((c) => c.id));
  const report = scope.entry(reportId)?.claim;
  if (!report || !visible.has(report.id)) return null;
  const linked = [...refersTo(scope, reportId, ['promote', 'flag', 'respond']), ...mentionedBy(scope, reportId, ['decide'])].filter((c) => visible.has(c.id));
  const decision = linked.find((c) => c.predicate === 'decide') ?? null;
  const appeal = decision ? (refersTo(scope, decision.id, ['appeal']).find((c) => visible.has(c.id)) ?? null) : null;
  const appealDecision = appeal ? (refersTo(scope, appeal.id, ['decide']).find((c) => visible.has(c.id)) ?? null) : null;
  const replies = [...linked.filter((c) => c.predicate === 'respond'), ...(decision ? refersTo(scope, decision.id, ['respond']).filter((c) => visible.has(c.id)) : [])];
  return {
    report,
    evidence: linked.filter((c) => c.predicate === 'promote'),
    flags: linked.filter((c) => c.predicate === 'flag'),
    decision,
    replies,
    appeal,
    appealDecision,
  };
}
